import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Receipt, Plus, Pencil, Trash2, Upload, Paperclip, Flag } from "lucide-react";
import api, { apiError, fileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fmtDate } from "@/lib/helpers";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { AttachPicker } from "@/components/Attachments";

const money = (n) => `£${Number(n || 0).toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const monthLabel = (m) => new Date(`${m}-01T00:00:00`).toLocaleDateString("en-GB", { month: "long", year: "numeric" });
const today = () => new Date().toISOString().slice(0, 10);
const blank = (pid) => ({ property_id: pid || "none", date: today(), description: "", category: "Other", amount: "", supplier: "", receipt: null });

function ExpenseDialog({ open, onClose, initial, props, categories, onSaved, propertyId }) {
  const [form, setForm] = useState(blank(propertyId));
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (open) { setForm(initial ? { ...initial, property_id: initial.property_id || "none", receipt: null } : blank(propertyId)); } }, [open, initial, propertyId]);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const r = new FileReader(); r.onload = () => set("receipt", r.result); r.readAsDataURL(f);
  };
  const submit = async () => {
    setSaving(true);
    const body = { ...form, amount: Number(form.amount), property_id: form.property_id === "none" ? null : form.property_id };
    try {
      if (initial) await api.put(`/expenses/${initial.id}`, body); else await api.post("/expenses", body);
      toast.success(initial ? "Expense updated" : "Expense added");
      onSaved(); onClose();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };
  const valid = form.description.trim() && Number(form.amount) > 0 && form.date;
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="expense-dialog">
        <DialogHeader><DialogTitle className="font-head">{initial ? "Edit expense" : "Add an expense"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="overline">Property</Label>
            <Select value={form.property_id} onValueChange={(v) => set("property_id", v)}>
              <SelectTrigger data-testid="expense-property" className="mt-1.5"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Not linked to a property (internal)</SelectItem>
                {props.map((p) => <SelectItem key={p.id} value={p.id}>{p.title}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div><Label className="overline">Description</Label><Textarea data-testid="expense-description" rows={2} value={form.description} onChange={(e) => set("description", e.target.value)} className="mt-1.5" placeholder="e.g. Replacement kettle and bin bags" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="overline">Amount (£)</Label><Input data-testid="expense-amount" type="number" min="0" step="0.01" value={form.amount} onChange={(e) => set("amount", e.target.value)} className="mt-1.5" /></div>
            <div><Label className="overline">Date</Label><Input data-testid="expense-date" type="date" value={form.date} onChange={(e) => set("date", e.target.value)} className="mt-1.5" /></div>
            <div>
              <Label className="overline">Category</Label>
              <Select value={form.category} onValueChange={(v) => set("category", v)}>
                <SelectTrigger data-testid="expense-category" className="mt-1.5"><SelectValue /></SelectTrigger>
                <SelectContent>{categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label className="overline">Supplier (optional)</Label><Input data-testid="expense-supplier" value={form.supplier || ""} onChange={(e) => set("supplier", e.target.value)} className="mt-1.5" /></div>
          </div>
          <label className="flex items-center gap-2 text-sm underline cursor-pointer"><Upload className="h-4 w-4" />{form.receipt ? "Receipt attached" : (initial?.receipt ? "Replace receipt" : "Attach receipt (optional)")}<input type="file" accept="image/*,application/pdf" className="hidden" data-testid="expense-receipt" onChange={onFile} /></label>
        </div>
        <DialogFooter><Button data-testid="expense-submit" onClick={submit} disabled={saving || !valid}><Receipt className="h-4 w-4 mr-1.5" />{initial ? "Save changes" : "Add expense"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function FlagDialog({ expense, onClose, onDone }) {
  const [reason, setReason] = useState("");
  const [files, setFiles] = useState([]);
  const [saving, setSaving] = useState(false);
  useEffect(() => { setReason(""); setFiles([]); }, [expense]);
  const submit = async () => {
    setSaving(true);
    try { await api.post(`/expenses/${expense.id}/flag`, { reason, attachments: files }); toast.success("Flagged — our team has been notified and will reply in Conversations"); onDone(); onClose(); }
    catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };
  return (
    <Dialog open={!!expense} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="expense-flag-dialog">
        <DialogHeader><DialogTitle className="font-head">Query this expense</DialogTitle></DialogHeader>
        <p className="text-sm text-muted-foreground">{expense?.description} · {money(expense?.amount)}</p>
        <Textarea data-testid="expense-flag-reason" rows={4} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="What's the problem with this expense?" />
        <AttachPicker files={files} onChange={setFiles} testId="expense-flag-attach" />
        <DialogFooter><Button data-testid="expense-flag-submit" onClick={submit} disabled={saving || !reason.trim()}><Flag className="h-4 w-4 mr-1.5" />Send to the team</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function FlagBadge({ e, nav }) {
  if (!e.flag_conversation_id) return null;
  const open = e.flag_status !== "resolved";
  return (
    <button data-testid={`expense-flag-badge-${e.id}`} onClick={() => nav(`/conversations?c=${e.flag_conversation_id}`)}
      className={`ml-2 text-[0.6rem] px-2 py-0.5 rounded-full inline-flex items-center gap-1 ${open ? "bg-[#9E382A]/12 text-[#9E382A]" : "bg-[#2B4C3F]/12 text-[#2B4C3F]"}`}>
      <Flag className="h-2.5 w-2.5" />{open ? "Queried by owner" : "Query resolved"}
    </button>
  );
}

function ExpenseRow({ e, canEdit, canFlag, onEdit, onDelete, onFlag, nav }) {
  return (
    <div data-testid={`expense-${e.id}`} className="flex items-start gap-3 px-4 py-3 border-t border-border first:border-t-0">
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium">{e.description}<FlagBadge e={e} nav={nav} /></p>
        <p className="text-xs text-muted-foreground mt-0.5">
          {fmtDate(e.date)} · {e.category}{e.supplier ? ` · ${e.supplier}` : ""} · {e.property_id
            ? <button data-testid={`expense-property-link-${e.id}`} className="underline hover:text-foreground" onClick={() => nav(`/properties/${e.property_id}`)}>{e.property_title}</button>
            : <span className="italic">Internal</span>}
          {e.receipt && <> · <a data-testid={`expense-receipt-link-${e.id}`} href={fileUrl(e.receipt)} target="_blank" rel="noreferrer" className="underline inline-flex items-center gap-0.5"><Paperclip className="h-3 w-3" />Receipt</a></>}
        </p>
      </div>
      <p className="text-sm font-semibold tabular-nums" data-testid={`expense-amount-${e.id}`}>{money(e.amount)}</p>
      {canFlag && !(e.flag_conversation_id && e.flag_status !== "resolved") && (
        <button data-testid={`expense-flag-${e.id}`} onClick={() => onFlag(e)} className="text-xs text-muted-foreground hover:text-[#9E382A] inline-flex items-center gap-1"><Flag className="h-3.5 w-3.5" />Query</button>
      )}
      {canEdit && (
        <div className="flex gap-1">
          <button data-testid={`expense-edit-${e.id}`} onClick={() => onEdit(e)} className="p-1 text-muted-foreground hover:text-foreground"><Pencil className="h-3.5 w-3.5" /></button>
          <button data-testid={`expense-delete-${e.id}`} onClick={() => onDelete(e)} className="p-1 text-muted-foreground hover:text-[#9E382A]"><Trash2 className="h-3.5 w-3.5" /></button>
        </div>
      )}
    </div>
  );
}

function MonthGroup({ month, items, ...rowProps }) {
  const total = items.reduce((s, e) => s + Number(e.amount || 0), 0);
  const byCat = items.reduce((m, e) => ({ ...m, [e.category]: (m[e.category] || 0) + Number(e.amount || 0) }), {});
  return (
    <div data-testid={`expense-month-${month}`} className="bg-white border border-border rounded-md overflow-hidden">
      <div className="flex items-center justify-between flex-wrap gap-2 px-4 py-3 bg-secondary/60">
        <div>
          <p className="font-head font-bold">{monthLabel(month)}</p>
          <p className="text-xs text-muted-foreground">{Object.entries(byCat).map(([c, v]) => `${c} ${money(v)}`).join(" · ")}</p>
        </div>
        <p className="font-head font-extrabold tabular-nums" data-testid={`expense-month-total-${month}`}>{money(total)}</p>
      </div>
      {items.map((e) => <ExpenseRow key={e.id} e={e} {...rowProps} />)}
    </div>
  );
}

export default function Expenses({ propertyId }) {
  const nav = useNavigate();
  const { user } = useAuth();
  const canEdit = ["admin", "operations"].includes(user.role);
  const canFlag = user.role === "landlord";
  const [flagging, setFlagging] = useState(null);
  const [items, setItems] = useState([]);
  const [props, setProps] = useState([]);
  const [categories, setCategories] = useState(["Other"]);
  const [propFilter, setPropFilter] = useState(propertyId || "all");
  const [monthFilter, setMonthFilter] = useState("all");
  const [dialog, setDialog] = useState({ open: false, initial: null });

  const load = () => api.get("/expenses", { params: propFilter === "all" ? {} : { property_id: propFilter } }).then((r) => setItems(r.data)).catch((e) => toast.error(apiError(e)));
  useEffect(() => { load(); }, [propFilter]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    api.get("/properties").then((r) => setProps(r.data)).catch(() => {});
    api.get("/expenses/meta").then((r) => setCategories(r.data.categories)).catch(() => {});
  }, []);

  const months = useMemo(() => [...new Set(items.map((e) => e.date.slice(0, 7)))], [items]);
  const shown = items.filter((e) => monthFilter === "all" || e.date.startsWith(monthFilter));
  const groups = useMemo(() => {
    const g = {};
    shown.forEach((e) => { (g[e.date.slice(0, 7)] = g[e.date.slice(0, 7)] || []).push(e); });
    return Object.entries(g).sort((a, b) => b[0].localeCompare(a[0]));
  }, [shown]);
  const total = shown.reduce((s, e) => s + Number(e.amount || 0), 0);

  const del = async (e) => {
    if (!window.confirm(`Delete expense "${e.description}"?`)) return;
    try { await api.delete(`/expenses/${e.id}`); toast.success("Expense deleted"); load(); } catch (err) { toast.error(apiError(err)); }
  };

  return (
    <div className="space-y-6" data-testid={propertyId ? "property-expenses" : "expenses-page"}>
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div className={propertyId ? "hidden" : ""}>
          <p className="overline text-accent flex items-center gap-1.5"><Receipt className="h-3.5 w-3.5" />Spending</p>
          <h1 className="font-head text-3xl font-extrabold tracking-tight mt-1">Expenses</h1>
          <p className="text-sm text-muted-foreground mt-1">{canEdit ? "Log ad-hoc spending and attribute it to a property. Owners see their properties' expenses by month." : "Spending on your properties, broken down by month."}</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {!propertyId && <Select value={propFilter} onValueChange={(v) => { setPropFilter(v); setMonthFilter("all"); }}>
            <SelectTrigger data-testid="expense-filter-property" className="w-52"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All properties</SelectItem>
              {canEdit && <SelectItem value="none">Internal only</SelectItem>}
              {props.map((p) => <SelectItem key={p.id} value={p.id}>{p.title}</SelectItem>)}
            </SelectContent>
          </Select>}
          <Select value={monthFilter} onValueChange={setMonthFilter}>
            <SelectTrigger data-testid="expense-filter-month" className="w-44"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All months</SelectItem>
              {months.map((m) => <SelectItem key={m} value={m}>{monthLabel(m)}</SelectItem>)}
            </SelectContent>
          </Select>
          {canEdit && <Button data-testid="new-expense-button" onClick={() => setDialog({ open: true, initial: null })}><Plus className="h-4 w-4 mr-1.5" />Add Expense</Button>}
        </div>
      </div>

      <div className="bg-white border border-border rounded-md px-4 py-3 flex items-center justify-between">
        <p className="text-sm text-muted-foreground">{shown.length} expense{shown.length === 1 ? "" : "s"} shown</p>
        <p className="font-head text-xl font-extrabold tabular-nums" data-testid="expense-total">{money(total)}</p>
      </div>

      {groups.length === 0 ? (
        <div className="p-12 text-center bg-white border border-border rounded-md" data-testid="expenses-empty">
          <Receipt className="h-8 w-8 mx-auto text-muted-foreground mb-3" />
          <p className="text-sm text-muted-foreground">No expenses to show.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {groups.map(([m, list]) => <MonthGroup key={m} month={m} items={list} canEdit={canEdit} canFlag={canFlag} onFlag={setFlagging} nav={nav} onEdit={(e) => setDialog({ open: true, initial: e })} onDelete={del} />)}
        </div>
      )}

      {canFlag && <FlagDialog expense={flagging} onClose={() => setFlagging(null)} onDone={load} />}
      {canEdit && <ExpenseDialog propertyId={propertyId} open={dialog.open} initial={dialog.initial} props={props} categories={categories} onClose={() => setDialog({ open: false, initial: null })} onSaved={load} />}
    </div>
  );
}
